import { useState, useCallback } from 'react';
import { Template, CustomNode, CustomEdge } from '../types';
import { useNotifications } from '../contexts/NotificationContext';
import { Node, Edge } from 'reactflow';
import { templateService, UpdateTemplateData } from '../services/template.service'; // Assuming UpdateTemplateData type exists

export function useTemplates() {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { showNotification } = useNotifications();

  const loadTemplates = useCallback(async (searchTerm?: string) => {
    setIsLoading(true);
    try {
      const serviceTemplates = await templateService.getAllTemplates(searchTerm);
      
      // Convert ServiceTemplate[] to Template[]
      const convertedTemplates: Template[] = serviceTemplates.map(template => ({
        ...template,
        // Convert Node[] to CustomNode[]
        nodes: template.nodes.map(node => node as unknown as CustomNode),
        // Convert Edge[] to CustomEdge[]
        edges: template.edges.map(edge => edge as unknown as CustomEdge)
      }));
      
      setTemplates(convertedTemplates);
      showNotification('success', 'Templates loaded successfully');
    } catch (error) {
      console.error('Failed to load templates:', error);
      showNotification('error', 'Failed to load templates.');
    } finally {
      setIsLoading(false);
    }
  }, [showNotification]);

  const saveTemplate = useCallback(
    async (template: Omit<Template, 'id' | 'created_at' | 'updated_at'>) => {
      setIsLoading(true);
      try {
        const data = await templateService.promoteToTemplate(template);
        return data;
      } catch (error) {
        console.error('Failed to save template:', error);
        showNotification('error', 'Failed to save template');
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    [showNotification],
  );

  const deleteTemplate = useCallback(
    async (id: string) => {
      setIsLoading(true);
      try {
        await templateService.deleteTemplate(id);
        setTemplates((prev) => prev.filter((t) => t.id !== id));
        showNotification('success', 'Template deleted successfully');
      } catch (error) {
        console.error('Failed to delete template:', error);
        showNotification('error', 'Failed to delete template');
      } finally {
        setIsLoading(false);
      }
    },
    [showNotification],
  );

  const promoteToTemplate = useCallback(
    async (project: {
      name: string;
      description: string;
      category: string;
      nodes: Node[];
      edges: Edge[];
      previewFile?: File;
    }) => {
      setIsLoading(true);
      try {
        const data = await templateService.promoteToTemplate(project);
        showNotification('success', 'Project promoted to template successfully');
        return data;
      } catch (error) {
        console.error('Failed to promote project to template:', error);
        showNotification('error', 'Failed to promote project to template');
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    [showNotification],
  );

  const updateTemplate = useCallback(
    async (id: string, data: UpdateTemplateData) => { // Assuming UpdateTemplateData type
      setIsLoading(true);
      try {
        const updatedTemplateFromService = await templateService.updateTemplate(id, data);
        
        // Convert the updated template's nodes and edges to CustomNode/CustomEdge
        const convertedUpdatedTemplate: Template = {
          ...updatedTemplateFromService,
          nodes: updatedTemplateFromService.nodes.map(node => node as unknown as CustomNode),
          edges: updatedTemplateFromService.edges.map(edge => edge as unknown as CustomEdge)
        };

        // Update the local state with the converted template
        setTemplates((prev) => 
          prev.map((t) => (t.id === id ? convertedUpdatedTemplate : t))
        );
        showNotification('success', 'Template updated successfully');
        return convertedUpdatedTemplate; // Return the converted template
      } catch (error) {
        console.error('Failed to update template:', error);
        showNotification('error', 'Failed to update template');
        throw error; // Re-throw the error after handling
      } finally {
        setIsLoading(false);
      }
    },
    [showNotification],
  );

  return {
    templates,
    isLoading,
    loadTemplates,
    saveTemplate,
    deleteTemplate,
    promoteToTemplate,
    updateTemplate, // Add updateTemplate here
  };
}
